const cards = [
  {
    id: 'cardiac',
    icon: 'ri-heart-pulse-line',
    title: 'Cardiac',
    tagline: 'Heart & Cardiovascular System',
    bg: 'bg-red-50',
    border: 'border-red-100',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    tagColor: 'text-red-400',
    accentBar: 'bg-red-400',
    items: [
      {
        icon: 'ri-heart-2-line',
        label: 'Heart Valve Issues',
        detail: 'Mitral, aortic, pulmonary, and tricuspid valve abnormalities are among the most common cardiac findings in CSCF.',
      },
      {
        icon: 'ri-pulse-line',
        label: 'Congenital Differences',
        detail: 'Septal defects (VSD, ASD, PFO), bicuspid aortic valve, aortic arch abnormalities, and pulmonary artery stenosis.',
      },
      {
        icon: 'ri-heart-pulse-line',
        label: 'Dilated Cardiomyopathy',
        detail: 'Newly reported in recent cases — an enlarged, weakened heart muscle that can present in infancy.',
      },
      {
        icon: 'ri-stethoscope-line',
        label: 'Cardiac Monitoring',
        detail: 'Lifelong cardiology follow-up is essential. Some individuals require surgical or catheter-based interventions.',
      },
    ],
  },
  {
    id: 'skeletal',
    icon: 'ri-body-scan-line',
    title: 'Skeletal',
    tagline: 'Bones, Spine & Joints',
    bg: 'bg-brand-50',
    border: 'border-brand-100',
    iconBg: 'bg-brand-100',
    iconColor: 'text-brand-500',
    tagColor: 'text-brand-400',
    accentBar: 'bg-brand-400',
    items: [
      {
        icon: 'ri-ruler-line',
        label: 'Short Stature',
        detail: 'Growth retardation is common, with some individuals significantly below average height for their age.',
      },
      {
        icon: 'ri-links-line',
        label: 'Bone Fusion',
        detail: 'Vertebral synostosis (cervical and thoracic), carpal and tarsal bone fusion are hallmark skeletal features of CSCF.',
      },
      {
        icon: 'ri-hand-sanitizer-line',
        label: 'Short Fingers & Toes',
        detail: 'Brachydactyly (shortened digits) is frequently observed, sometimes alongside joint laxity or contractures.',
      },
      {
        icon: 'ri-layout-column-line',
        label: 'Scoliosis & Pectus',
        detail: 'Spinal curvature (scoliosis) and pectus excavatum (sunken chest) are reported in multiple cases.',
      },
    ],
  },
  {
    id: 'hearing',
    icon: 'ri-volume-mute-line',
    title: 'Hearing',
    tagline: 'Auditory System',
    bg: 'bg-accent-50',
    border: 'border-accent-100',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-500',
    tagColor: 'text-accent-400',
    accentBar: 'bg-accent-400',
    items: [
      {
        icon: 'ri-volume-down-line',
        label: 'Conductive Hearing Loss',
        detail: 'Bilateral conductive hearing loss is present in most individuals with CSCF, often from birth or early infancy.',
      },
      {
        icon: 'ri-virus-line',
        label: 'Ear Infections',
        detail: 'Recurrent otitis media (middle ear infections) is common and often requires tympanostomy tube placement.',
      },
      {
        icon: 'ri-sound-module-line',
        label: 'Inner Ear Malformation',
        detail: 'Structural abnormalities of the inner ear and stenosis of the external auditory canal have been documented.',
      },
      {
        icon: 'ri-service-line',
        label: 'Audiological Support',
        detail: 'Hearing aids, cochlear implant evaluation, and speech therapy are important components of care.',
      },
    ],
  },
  {
    id: 'development',
    icon: 'ri-seedling-line',
    title: 'Development',
    tagline: 'Growth, Feeding & Facial Features',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-500',
    tagColor: 'text-emerald-400',
    accentBar: 'bg-emerald-400',
    items: [
      {
        icon: 'ri-restaurant-line',
        label: 'Feeding Challenges',
        detail: 'Feeding difficulties from birth are very common. Some infants require nasogastric or gastrostomy tube (G-tube) feeding.',
      },
      {
        icon: 'ri-bar-chart-grouped-line',
        label: 'Growth Delays',
        detail: 'Global developmental delay affecting gross motor, fine motor, and speech milestones is frequently reported.',
      },
      {
        icon: 'ri-emotion-line',
        label: 'Facial Features',
        detail: 'Prominent forehead, widely spaced eyes, downslanted palpebral fissures, broad nasal bridge, and low-set ears.',
      },
      {
        icon: 'ri-mental-health-line',
        label: 'Hypotonia',
        detail: 'Low muscle tone (hypotonia) is common and contributes to motor delays. Early physical therapy is beneficial.',
      },
    ],
  },
];

export default function SymptomsGrid() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* 4-column equal card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`rounded-2xl border ${card.bg} ${card.border} flex flex-col overflow-hidden`}
            >
              {/* Accent top bar */}
              <div className={`h-1 w-full ${card.accentBar}`} />

              {/* Card header */}
              <div className="p-6 pb-4">
                <div className={`w-12 h-12 flex items-center justify-center ${card.iconBg} rounded-xl mb-4`}>
                  <i className={`${card.icon} text-2xl ${card.iconColor}`}></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900 font-heading mb-1">{card.title}</h2>
                <p className={`text-xs font-semibold uppercase tracking-wider font-heading ${card.tagColor}`}>
                  {card.tagline}
                </p>
              </div>

              {/* Divider */}
              <div className={`mx-6 border-t ${card.border}`} />

              {/* Items list */}
              <div className="p-6 pt-5 flex flex-col gap-5 flex-grow">
                {card.items.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className={`w-7 h-7 flex items-center justify-center ${card.iconBg} rounded-lg flex-shrink-0 mt-0.5`}>
                      <i className={`${item.icon} text-sm ${card.iconColor}`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 font-heading leading-snug mb-0.5">
                        {item.label}
                      </p>
                      <p className="text-xs text-gray-500 font-body leading-relaxed">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional systems strip */}
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8 mb-20">
          <h3 className="text-base font-bold text-gray-900 font-heading mb-5 flex items-center gap-2">
            <i className="ri-add-circle-line text-brand-500"></i>
            Additional Features Reported in Some Cases
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: 'ri-lungs-line', label: 'Pulmonary', text: 'Recurrent respiratory infections; thoracic vertebral fusion can restrict lung growth' },
              { icon: 'ri-hand-coin-line', label: 'Skin', text: 'Soft, velvety skin; wrinkled palms and soles; skin laxity' },
              { icon: 'ri-eye-line', label: 'Eyes', text: 'Ptosis, strabismus, and downslanted palpebral fissures' },
              { icon: 'ri-store-3-line', label: 'Gastrointestinal', text: 'Reflux, dysmotility, intestinal obstruction (rare)' },
              { icon: 'ri-shield-cross-line', label: 'Immune', text: 'Possible immune dysregulation; inflammatory bowel disease reported in one case' },
              { icon: 'ri-brain-line', label: 'Neurological', text: 'Normal brain MRI in most cases; mild intellectual disability in some' },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-gray-500 text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 font-heading">{item.label}</p>
                  <p className="text-xs text-gray-500 font-body leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom centered highlight */}
        <div className="text-center mb-16">
          <div className="inline-block max-w-2xl">
            <div className="w-14 h-14 flex items-center justify-center bg-brand-50 rounded-full mx-auto mb-6">
              <i className="ri-user-heart-line text-brand-500 text-2xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading mb-4">
              No two cases are the same.
            </h2>
            <p className="text-gray-500 text-lg font-body leading-relaxed mb-8">
              CSCF presents differently in every individual. Some may have primarily cardiac involvement, others skeletal or developmental. Severity ranges from mild to life-threatening. This variability is why personalized, multidisciplinary care matters so much.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/science#cases"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body"
              >
                <i className="ri-file-paper-2-line"></i>
                Read Published Cases
              </a>
              <a
                href="/about"
                className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-brand-300 hover:text-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body"
              >
                <i className="ri-information-line"></i>
                About CSCF
              </a>
            </div>
          </div>
        </div>

        {/* Genetic testing callout */}
        <div className="bg-gradient-to-br from-emerald-50 to-brand-50 border border-emerald-100 rounded-3xl p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 border border-emerald-200 rounded-full mb-4">
                <i className="ri-test-tube-line text-emerald-600 text-xs"></i>
                <span className="text-xs font-bold text-emerald-700 tracking-widest font-heading">IMPORTANT</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 font-heading mb-3">
                Recognizing symptoms is just the first step.
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed font-body mb-4">
                These features can overlap with many other conditions. <strong className="text-gray-800">Only whole exome sequencing (WES) identifying a pathogenic MAP3K7 variant can confirm a CSCF diagnosis.</strong> If your child has several of these features, speak to a geneticist about WES.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed font-body">
                A confirmed genetic diagnosis is the gateway to the right specialists, the right monitoring, and connection to the only community in the world that truly understands.
              </p>
            </div>
            <div className="space-y-3">
              {[
                { icon: 'ri-error-warning-line', color: 'text-amber-500', bg: 'bg-amber-50', text: 'CSCF is frequently misdiagnosed as Noonan syndrome or other RASopathies due to overlapping features' },
                { icon: 'ri-test-tube-line', color: 'text-emerald-600', bg: 'bg-emerald-50', text: 'Trio-WES (child + both parents) is the most effective diagnostic approach and has confirmed every known case' },
                { icon: 'ri-parent-line', color: 'text-brand-500', bg: 'bg-brand-50', text: 'Most variants are de novo — not inherited — so family history alone won\'t raise a red flag' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                  <div className={`w-8 h-8 flex items-center justify-center ${item.bg} rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} ${item.color} text-sm`}></i>
                  </div>
                  <p className="text-sm text-gray-600 font-body leading-relaxed">{item.text}</p>
                </div>
              ))}
              <a
                href="/science#diagnosis"
                className="inline-flex items-center gap-2 mt-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
              >
                <i className="ri-microscope-line"></i>
                Read the Full Diagnosis Guide
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
